import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import {
    FileText,
    Download,
    Eye,
    AlertCircle,
    LogOut,
    Loader2,
    FolderOpen,
} from "lucide-react";
import { COMPANY } from "@/lib/compliance";
import {
    clearSession,
    fetchDocumentLink,
    fetchDocuments,
    getSession,
} from "@/lib/portalApi";

const SIZE_UNITS = ["B", "KB", "MB", "GB"];
function formatSize(bytes) {
    if (!bytes && bytes !== 0) return "—";
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < SIZE_UNITS.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${SIZE_UNITS[i]}`;
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch (_) {
        return "—";
    }
}

/**
 * Display order for known document categories. Anything not listed sorts
 * after these in alphabetical order. Mirrors the recognised folder names
 * in /app/backend/portal_dropbox.py so the customer experience stays
 * consistent regardless of how files are organised in Dropbox.
 */
const CATEGORY_ORDER = [
    "Policy Declarations",
    "ID Cards",
    "Endorsements",
    "Binders",
    "Certificates of Insurance",
    "Quotes",
    "Applications",
    "Claims",
    "Receipts",
    "Invoices",
    "Statements",
    "Correspondence",
    "Other Documents",
];

function groupByCategory(docs) {
    const buckets = new Map();
    for (const d of docs) {
        const key = d.category || "Other Documents";
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(d);
    }
    // Sort within each bucket: most recently modified first.
    for (const list of buckets.values()) {
        list.sort((a, b) => (b.modified || "").localeCompare(a.modified || ""));
    }
    // Stable category ordering: known categories in the canonical order, then
    // any custom ones alphabetically.
    const known = CATEGORY_ORDER.filter((c) => buckets.has(c));
    const extras = [...buckets.keys()]
        .filter((c) => !CATEGORY_ORDER.includes(c))
        .sort((a, b) => a.localeCompare(b));
    return [...known, ...extras].map((category) => ({
        category,
        files: buckets.get(category),
    }));
}

/**
 * /customer-portal/dashboard — authenticated document list.
 */
export default function CustomerPortalDashboardPage() {
    const navigate = useNavigate();
    const [session, setSessionState] = useState(getSession());
    const [docs, setDocs] = useState(null);
    const [error, setError] = useState(null);
    const [mocked, setMocked] = useState(false);
    const [busyPath, setBusyPath] = useState(null);

    useEffect(() => {
        document.title = "Customer Portal — Endurance Casualty";
        if (!session?.token) {
            navigate("/customer-portal/login", { replace: true });
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchDocuments();
                if (cancelled) return;
                setDocs(data.documents || []);
                setMocked(!data.dropbox_configured);
            } catch (e) {
                if (cancelled) return;
                if (e?.response?.status === 401) {
                    clearSession();
                    navigate("/customer-portal/login", { replace: true });
                    return;
                }
                setError(
                    (typeof e?.response?.data?.detail === "string" &&
                        e.response.data.detail) ||
                        "We could not load your documents right now. Please try again shortly."
                );
                setDocs([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [session, navigate]);

    const handleSignOut = () => {
        clearSession();
        setSessionState(null);
        navigate("/customer-portal/login", { replace: true });
    };

    const handleAction = async (rel, mode) => {
        try {
            setBusyPath(rel);
            const { url } = await fetchDocumentLink(rel);
            if (!url || url.startsWith("about:blank")) {
                setError(
                    "This is a sample preview — real document downloads will be available once the agency finishes connecting the secure document store. Please call us anytime and we can email a copy to you."
                );
                return;
            }
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (e) {
            setError(
                (typeof e?.response?.data?.detail === "string" &&
                    e.response.data.detail) ||
                    "Could not generate a secure link for this document."
            );
        } finally {
            setBusyPath(null);
        }
    };

    if (!session) return null;

    return (
        <main
            data-testid="page-portal-dashboard"
            className="min-h-screen bg-brand-bg text-brand-ivory"
        >
            <PageHeader />

            <section className="mx-auto max-w-[1200px] px-6 py-12 md:px-10 md:py-16">
                {/* Header row */}
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.45em] text-brand-goldSoft">
                            Customer Portal
                        </p>
                        <h1 className="mt-2 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl">
                            Your Documents
                        </h1>
                        <p
                            data-testid="portal-dashboard-user"
                            className="mt-2 text-sm text-brand-muted"
                        >
                            Signed in as{" "}
                            <strong className="text-brand-ivory">
                                {session.user?.display_name || session.user?.email}
                            </strong>
                            <span className="mx-2">·</span>
                            Customer ID{" "}
                            <span className="font-mono text-brand-ivory">
                                {session.user?.customer_id}
                            </span>
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="portal-signout"
                        onClick={handleSignOut}
                        className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        <LogOut aria-hidden="true" className="h-3.5 w-3.5" />
                        Sign out
                    </button>
                </div>

                {/* Mock-mode banner */}
                {mocked && (
                    <div
                        data-testid="portal-mock-banner"
                        role="status"
                        className="mt-6 flex items-start gap-3 rounded-lg border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.05)] px-4 py-3 text-xs leading-relaxed text-brand-ivory/85"
                    >
                        <AlertCircle
                            aria-hidden="true"
                            className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold2"
                        />
                        <span>
                            <strong>Preview mode.</strong> Dropbox isn&apos;t
                            connected yet, so the documents below are a sample
                            set. Once the agency connects Dropbox, your real
                            files will appear here automatically.
                        </span>
                    </div>
                )}

                {error && (
                    <div
                        data-testid="portal-dashboard-error"
                        role="alert"
                        className="mt-6 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-200"
                    >
                        <AlertCircle
                            aria-hidden="true"
                            className="mt-0.5 h-4 w-4 shrink-0 text-red-300"
                        />
                        <span>{error}</span>
                    </div>
                )}

                {/* Document table */}
                <div className="mt-8 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13]">
                    {docs === null ? (
                        <div className="flex items-center gap-3 px-5 py-8 text-sm text-brand-muted">
                            <Loader2
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin text-brand-gold2"
                            />
                            Loading your documents…
                        </div>
                    ) : docs.length === 0 ? (
                        <div
                            data-testid="portal-empty"
                            className="px-5 py-10 text-center"
                        >
                            <FolderOpen
                                aria-hidden="true"
                                className="mx-auto h-8 w-8 text-brand-goldSoft"
                            />
                            <p className="mt-3 font-serif text-lg text-brand-ivory">
                                No documents yet
                            </p>
                            <p className="mt-1 text-sm text-brand-muted">
                                Your agent will upload your policy documents
                                here. Check back soon, or call{" "}
                                <a
                                    href={`tel:${COMPANY.phoneTel}`}
                                    className="text-brand-gold2 underline-offset-4 hover:underline"
                                >
                                    {COMPANY.phone}
                                </a>
                                .
                            </p>
                        </div>
                    ) : (
                        <div
                            data-testid="portal-documents-table"
                            className="divide-y divide-[rgba(212,175,55,0.1)]"
                        >
                            {groupByCategory(docs).map(({ category, files }) => (
                                <DocumentGroup
                                    key={category}
                                    category={category}
                                    files={files}
                                    busyPath={busyPath}
                                    handleAction={handleAction}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <p className="mt-6 text-xs leading-relaxed text-brand-muted">
                    Documents are streamed securely through Dropbox using
                    short-lived links. Your sign-in expires after 8 hours of
                    inactivity. Need help?{" "}
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        Call {COMPANY.phone}
                    </a>{" "}
                    or{" "}
                    <Link
                        to="/contact"
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        send a message
                    </Link>
                    .
                </p>
            </section>

            <FooterBar />
        </main>
    );
}

/**
 * A single category section: heading + count, then the files in that
 * category rendered as a clean list. Used inside the dashboard.
 */
function DocumentGroup({ category, files, busyPath, handleAction }) {
    return (
        <section
            data-testid="portal-category-section"
            data-category={category}
            className="px-5 py-5"
        >
            <header className="mb-3 flex items-baseline justify-between gap-3">
                <h3
                    data-testid="portal-category-heading"
                    className="font-serif text-base text-brand-ivory md:text-lg"
                >
                    {category}
                </h3>
                <span className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">
                    {files.length} {files.length === 1 ? "file" : "files"}
                </span>
            </header>
            <ul className="divide-y divide-[rgba(212,175,55,0.08)] rounded-xl border border-[rgba(212,175,55,0.12)] bg-[rgba(255,255,255,0.015)]">
                {files.map((d) => (
                    <li
                        key={d.relative_path}
                        data-testid="portal-document-row"
                        className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-[rgba(212,175,55,0.04)]"
                    >
                        <FileText
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0 text-brand-gold2"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-brand-ivory">
                                {d.name}
                            </p>
                            <p className="mt-0.5 text-[11px] text-brand-muted">
                                Updated {formatDate(d.modified)} · {formatSize(d.size)}
                            </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                            <button
                                type="button"
                                onClick={() => handleAction(d.relative_path, "view")}
                                disabled={busyPath === d.relative_path}
                                className="inline-flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.4)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg disabled:cursor-wait disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                                data-testid="portal-doc-view"
                            >
                                <Eye aria-hidden="true" className="h-3 w-3" />
                                View
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    handleAction(d.relative_path, "download")
                                }
                                disabled={busyPath === d.relative_path}
                                className="inline-flex items-center gap-1 rounded-full border border-brand-gold bg-brand-gold px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-bg transition-colors hover:bg-brand-gold2 disabled:cursor-wait disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                                data-testid="portal-doc-download"
                            >
                                <Download aria-hidden="true" className="h-3 w-3" />
                                Download
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
